import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  CheckCircleOutline,
  ErrorOutline,
  LockOutlined,
  Security,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Container,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { APIEndpoint } from 'common/enum';
import { universalPostRequest } from 'common/requestHandler';
import { popupNotification } from 'notifications/ToastNotification';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'redux/store';

import { passwordSchema } from './utils';
import CustomModal from '../../components/CustomModal';
import BackButton from '../../components/elements/BackButton';
import GradientButton from '../../components/elements/GradientButton';
import InputElement from '../../components/elements/InputElement';
import { logout } from '../../redux/slices/authSlice';
import colors from '../../theme/colors';

export interface ChangePassword {
  currentPassword: string;
  password: string;
  confirmPassword: string;
}

const ChangePassword = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const { username } = useSelector((state: RootState) => state.auth);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [responseMessage, setResponseMessage] = useState<string>('');
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    password: false,
    confirmPassword: false,
  });

  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<ChangePassword>({
    resolver: yupResolver(passwordSchema),
    mode: 'onChange',
    defaultValues: {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
  });

  const watchedPassword = watch('password');
  const watchedConfirmPassword = watch('confirmPassword');

  const handleTogglePasswordVisibility = (field: keyof ChangePassword) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const onSubmit: SubmitHandler<ChangePassword> = async (
    data: ChangePassword
  ) => {
    setLoading(true);
    const response = await universalPostRequest(APIEndpoint.changePassword, {
      username: username,
      previousPassword: data.currentPassword,
      proposedPassword: data.password,
    });
    setLoading(false);
    if ('error' in response) {
      popupNotification(response.error, false);
    } else {
      setShowModal(true);
      setResponseMessage(response.message);
    }
  };

  const PasswordStrengthIndicator = () => {
    if (!watchedPassword) return null;

    const hasLength = watchedPassword.length >= 8;
    const hasUpperCase = /[A-Z]/.test(watchedPassword);
    const hasLowerCase = /[a-z]/.test(watchedPassword);
    const hasNumber = /\d/.test(watchedPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(watchedPassword);

    const criteria = [
      { label: 'At least 8 characters', met: hasLength },
      { label: 'One uppercase letter', met: hasUpperCase },
      { label: 'One lowercase letter', met: hasLowerCase },
      { label: 'One number', met: hasNumber },
      { label: 'One special character', met: hasSpecialChar },
    ];

    const metCount = criteria.filter((c) => c.met).length;
    const strength = metCount < 3 ? 'weak' : metCount < 5 ? 'medium' : 'strong';

    return (
      <Fade in={!!watchedPassword}>
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: alpha(colors.primary, 0.08),
            borderRadius: 2,
            border: `1px solid ${alpha(colors.primary, 0.2)}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, mb: 1, display: 'block' }}
          >
            Password Strength:{' '}
            <span
              style={{
                color:
                  strength === 'strong'
                    ? colors.gradientColor1
                    : strength === 'medium'
                      ? colors.warning
                      : colors.errorMain,
              }}
            >
              {strength.charAt(0).toUpperCase() + strength.slice(1)}
            </span>
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {criteria.map((criterion, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                {criterion.met ? (
                  <CheckCircleOutline
                    sx={{ fontSize: 16, color: colors.gradientColor1 }}
                  />
                ) : (
                  <ErrorOutline
                    sx={{ fontSize: 16, color: colors.errorMain }}
                  />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: criterion.met ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {criterion.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Fade>
    );
  };

  const PasswordMatchIndicator = () => {
    if (!watchedPassword || !watchedConfirmPassword) return null;

    const isMatch = watchedPassword === watchedConfirmPassword;

    return (
      <Fade in={!!watchedConfirmPassword}>
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMatch ? (
            <CheckCircleOutline
              sx={{ fontSize: 16, color: colors.gradientColor1 }}
            />
          ) : (
            <ErrorOutline sx={{ fontSize: 16, color: colors.errorMain }} />
          )}
          <Typography
            variant="caption"
            sx={{
              color: isMatch ? colors.gradientColor1 : colors.errorMain,
            }}
          >
            {isMatch ? 'Passwords match' : 'Passwords do not match'}
          </Typography>
        </Box>
      </Fade>
    );
  };

  return (
    <>
      <Box>
        <BackButton />
      </Box>
      <Container maxWidth="sm">
        <Fade in timeout={600}>
          <Box>
            {/* Back Button */}

            {/* Header Section */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${colors.gradientColor1}, ${colors.gradientColor2})`,
                  mb: 2,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                }}
              >
                <Security sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  background: `linear-gradient(135deg, ${colors.gradientColor1}, ${colors.gradientColor2})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Change Password
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', maxWidth: 400, mx: 'auto' }}
              >
                Update your password to keep your account secure. Make sure to
                use a strong password that you haven't used before.
              </Typography>
            </Box>

            {/* Form Card */}
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(colors.primary, 0.02)}, ${alpha(colors.gradientColor1, 0.02)})`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(colors.primary, 0.1)}`,
                boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${colors.gradientColor1}, ${colors.gradientColor2})`,
                },
              }}
            >
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* Current Password Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <LockOutlined sx={{ fontSize: 18 }} />
                    Current Password
                  </Typography>
                  <InputElement
                    name="currentPassword"
                    control={control}
                    label="Enter your current password"
                    type={showPassword.currentPassword ? 'text' : 'password'}
                    rules={{ required: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: `0 0 0 2px ${alpha(colors.primary, 0.2)}`,
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha(colors.primary, 0.3)}`,
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              handleTogglePasswordVisibility('currentPassword')
                            }
                            edge="end"
                            sx={{
                              color: 'text.secondary',
                              '&:hover': { color: theme.palette.primary.main },
                            }}
                          >
                            {showPassword.currentPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Divider sx={{ my: 3, opacity: 0.6 }} />

                {/* New Password Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Security sx={{ fontSize: 18 }} />
                    New Password
                  </Typography>
                  <InputElement
                    name="password"
                    control={control}
                    label="Enter your new password"
                    type={showPassword.password ? 'text' : 'password'}
                    rules={{ required: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              handleTogglePasswordVisibility('password')
                            }
                            edge="end"
                            sx={{
                              color: 'text.secondary',
                              '&:hover': { color: theme.palette.primary.main },
                            }}
                          >
                            {showPassword.password ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <PasswordStrengthIndicator />
                </Box>

                {/* Confirm Password Section */}
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <LockOutlined sx={{ fontSize: 18 }} />
                    Confirm New Password
                  </Typography>
                  <InputElement
                    name="confirmPassword"
                    control={control}
                    label="Confirm your new password"
                    type={showPassword.confirmPassword ? 'text' : 'password'}
                    rules={{ required: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              handleTogglePasswordVisibility('confirmPassword')
                            }
                            edge="end"
                            sx={{
                              color: 'text.secondary',
                              '&:hover': { color: theme.palette.primary.main },
                            }}
                          >
                            {showPassword.confirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <PasswordMatchIndicator />
                </Box>

                {/* Submit Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <GradientButton
                    type="submit"
                    rounded
                    loading={loading}
                    loadingText="Updating password..."
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      minWidth: 200,
                      '&:disabled': {
                        opacity: 0.6,
                      },
                    }}
                  >
                    Update Password
                  </GradientButton>
                </Box>
              </form>
            </Paper>

            {/* Security Tips */}
            <Box
              sx={{
                mt: 4,
                p: 3,
                bgcolor: alpha(colors.primary, 0.05),
                borderRadius: 2,
                border: `1px solid ${alpha(colors.primary, 0.2)}`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1, color: colors.primary }}
              >
                💡 Security Tips
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', lineHeight: 1.6 }}
              >
                • Use a unique password that you haven't used elsewhere
                <br />
                • Include a mix of uppercase, lowercase, numbers, and special
                characters
                <br />
                • Avoid using personal information like birthdays or names
                <br />• Consider using a password manager for better security
              </Typography>
            </Box>
          </Box>
        </Fade>

        {/* Success Modal */}
        <CustomModal
          title={responseMessage}
          type={responseMessage.length ? 'success' : 'error'}
          open={showModal}
          primaryButtonLabel={responseMessage.length ? 'Login' : 'Try again'}
          primaryButtonAction={() => {
            dispatch(logout());
            navigate('/login');
          }}
          onClose={() => {
            setShowModal(false);
            setResponseMessage('');
          }}
        >
          <Typography variant="body2">{responseMessage}</Typography>
        </CustomModal>
      </Container>
    </>
  );
};

export default ChangePassword;
